/**
 * Plugin Data API
 * Provides functions for managing plugin data at different scopes
 */
import {
  UrlValidationResult,
  validateUrl,
} from "@fuse/plugins/security/urlValidator"
import { httpsCallable } from "firebase/functions"

import { firebaseFunctions, functionsAccessor } from "@firebaseConfig/clientApp"

/**
 * Standard API response type
 */
export interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

/**
 * Raw Plugin API Interface - Base operations without bound parameters
 */
export interface RawPluginApi {
  // Plugin configuration management
  getPluginConfig: (
    eventId: string,
    pluginId: string,
  ) => Promise<ApiResponse<any>>
  savePluginConfig: (
    eventId: string,
    pluginId: string,
    config: any,
  ) => Promise<ApiResponse<void>>

  // Event instance plugin data storage
  getPluginEventInstanceData: (
    eventId: string,
    pluginId: string,
  ) => Promise<ApiResponse<any>>
  savePluginEventInstanceData: (
    eventId: string,
    pluginId: string,
    data: any,
  ) => Promise<ApiResponse<void>>

  // Event plugin user data storage
  getEventPluginUserData: (
    eventId: string,
    pluginId: string,
    userId: string,
  ) => Promise<ApiResponse<any>>
  saveEventPluginUserData: (
    eventId: string,
    pluginId: string,
    userId: string,
    data: any,
  ) => Promise<ApiResponse<void>>
}

/**
 * Bound Plugin API Interface - Operations with pre-bound parameters
 *
 * SYNC REQUIREMENT: This interface must be kept in sync with the Monaco editor type definitions
 * in apps/website/src/@fuse/pages/plugins/types/pluginEditorTypes.ts (PluginAPI.API interface)
 *
 * When modifying this interface:
 * 1. Update the corresponding type string in pluginEditorTypes.ts
 * 2. Ensure method signatures match exactly
 * 3. Update any JSDoc comments to maintain consistency
 */
export interface BoundPluginApi {
  // Plugin configuration management
  getPluginConfig: () => Promise<any>
  savePluginConfig: (config: any) => Promise<void>

  // Event instance plugin data storage
  getPluginEventInstanceData: () => Promise<any>
  savePluginEventInstanceData: (data: any) => Promise<void>

  // Event plugin user data storage
  getEventPluginUserData: () => Promise<any>
  saveEventPluginUserData: (data: any) => Promise<void>

  // Navigation methods with security validation
  openUrl: (url: string, target?: "_blank" | "_self") => UrlValidationResult
  validateUrl: (url: string) => UrlValidationResult

  // Context information
  getEventId: () => string
  getPluginId: () => string
}

/**
 * Helper to handle API responses
 */
function handleApiResponse<T>(result: ApiResponse<T>, errorMsg: string): T {
  if (result.success) {
    return result.data
  }
  throw new Error(result.error || errorMsg)
}

/**
 * Creates a bound plugin API from a raw API by binding eventId, pluginId, and userId
 */
export function bindPluginApi(
  rawApi: RawPluginApi,
  eventId: string,
  pluginId: string,
  userId: string,
): BoundPluginApi {
  console.log(
    `[PLUGIN] [pluginDataApi] bindPluginApi called for plugin: ${pluginId}, event: ${eventId}, user: ${userId}`,
  )

  return {
    // Plugin configuration
    getPluginConfig: async () => {
      console.log(
        `[PLUGIN] [pluginDataApi] boundApi.getPluginConfig called for plugin: ${pluginId}`,
      )
      const result = await rawApi.getPluginConfig(eventId, pluginId)
      return handleApiResponse(result, "Failed to get plugin config")
    },
    savePluginConfig: async (config) => {
      console.log(
        `[PLUGIN] [pluginDataApi] boundApi.savePluginConfig called for plugin: ${pluginId}`,
      )
      const result = await rawApi.savePluginConfig(eventId, pluginId, config)
      handleApiResponse(result, "Failed to save plugin config")
    },

    // Event instance plugin data
    getPluginEventInstanceData: async () => {
      console.log(
        `[PLUGIN] [pluginDataApi] boundApi.getPluginEventInstanceData called for plugin: ${pluginId}`,
      )
      const result = await rawApi.getPluginEventInstanceData(eventId, pluginId)
      return {
        ...handleApiResponse(
          result,
          "Failed to get plugin event instance data",
        ),
        eventId,
        pluginId,
        userId,
      }
    },
    savePluginEventInstanceData: async (data) => {
      console.log(
        `[PLUGIN] [pluginDataApi] boundApi.savePluginEventInstanceData called for plugin: ${pluginId}`,
      )
      const result = await rawApi.savePluginEventInstanceData(
        eventId,
        pluginId,
        data,
      )
      handleApiResponse(result, "Failed to save plugin event instance data")
    },

    // Event plugin user data
    getEventPluginUserData: async () => {
      console.log(
        `[PLUGIN] [pluginDataApi] boundApi.getEventPluginUserData called for plugin: ${pluginId}`,
      )
      const result = await rawApi.getEventPluginUserData(
        eventId,
        pluginId,
        userId,
      )
      return handleApiResponse(result, "Failed to get event plugin user data")
    },
    saveEventPluginUserData: async (data) => {
      console.log(
        `[PLUGIN] [pluginDataApi] boundApi.saveEventPluginUserData called for plugin: ${pluginId}`,
      )
      const result = await rawApi.saveEventPluginUserData(
        eventId,
        pluginId,
        userId,
        data,
      )
      handleApiResponse(result, "Failed to save event plugin user data")
    },

    // Navigation methods with security validation
    openUrl: (url: string, target: "_blank" | "_self" = "_blank") => {
      const validation = validateUrl(url)

      if (!validation.isValid || !validation.isAllowed) {
        console.warn("URL blocked by security policy:", validation.reason)
        return validation
      }

      if (typeof window !== "undefined") {
        // Ensure URL has protocol for external navigation
        let fullUrl = url.trim()
        if (fullUrl && !fullUrl.match(/^[a-zA-Z][a-zA-Z\d+\-.]*:/)) {
          fullUrl = "https://" + fullUrl
        }

        window.open(fullUrl, target, "noopener,noreferrer")
      }

      return validation
    },
    validateUrl: (url: string) => {
      return validateUrl(url)
    },

    // Context information
    getEventId: () => eventId,
    getPluginId: () => pluginId,
  }
}

// Implementation of the raw plugin API functions
export const getPluginConfig = async (
  eventId: string,
  pluginId: string,
): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `[PLUGIN] [pluginDataApi] Getting plugin config for: ${pluginId} in event: ${eventId}`,
    )

    // Check if plugin is already in Recoil event plugins state (from useEventPlugins)
    try {
      const { getRecoil } = await import("recoil-nexus")
      const { eventPluginsAtom } = await import("@fuse/recoil/eventPlugins")
      const eventPluginsState = getRecoil(eventPluginsAtom)
      const eventState = eventPluginsState.events[eventId]
      const cachedPlugin = eventState?.plugins[pluginId]

      if (cachedPlugin) {
        console.log(
          `[PLUGIN] [pluginDataApi] Found plugin in Recoil event plugins state, using cached config for plugin: ${pluginId}`,
        )
        return {
          success: true,
          data: eventState.configurations[pluginId] || {},
        }
      }
    } catch (recoilError) {
      console.log(
        `[PLUGIN] [pluginDataApi] Could not access Recoil state, falling back to API call:`,
        recoilError,
      )
    }

    console.log(
      `[PLUGIN] [pluginDataApi] Plugin not found in Recoil cache, making API call for plugin: ${pluginId}`,
    )

    // Use existing getPluginDetails function to get configuration
    const { getPluginDetails } = await import("./pluginApi")
    console.log(
      `[PLUGIN] [pluginDataApi] About to call getPluginDetails for plugin: ${pluginId}`,
    )
    const result = await getPluginDetails({ eventId, pluginId })
    console.log(
      `[PLUGIN] [pluginDataApi] getPluginDetails returned for plugin: ${pluginId}`,
      result.success,
    )

    if (result.success && (result.data as any)?.configuration) {
      return {
        success: true,
        data: (result.data as any).configuration,
      }
    } else {
      return {
        success: true,
        data: {}, // Return empty config if none exists
      }
    }
  } catch (error) {
    console.error("Error getting plugin config:", error)
    return {
      success: false,
      data: {},
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export const savePluginConfig = async (
  eventId: string,
  pluginId: string,
  config: any,
): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `[pluginDataApi] Saving plugin config for: ${pluginId} in event: ${eventId}`,
    )

    // Use existing updatePluginInstance function to save configuration
    const { createOrUpdatePluginInstance } = await import("./pluginApi")
    const result = await createOrUpdatePluginInstance({
      eventId,
      pluginId,
      configuration: config,
      enabled: true, // Assume enabled when saving config
    })

    if (result.success) {
      return {
        success: true,
        data: config,
      }
    } else {
      return {
        success: false,
        data: {},
        error: result.error || "Failed to save plugin configuration",
      }
    }
  } catch (error) {
    console.error("Error saving plugin config:", error)
    return {
      success: false,
      data: {},
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export const getPluginEventInstanceData = async (
  eventId: string,
  pluginId: string,
): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `[pluginDataApi] Getting plugin event instance data for: ${pluginId} in event: ${eventId}`,
    )

    // Call the backend function for event instance data
    const getPluginEventInstanceDataFunction = functionsAccessor.httpsCallable(
      "pluginManagement-getPluginEventInstanceData",
    )
    const result = await getPluginEventInstanceDataFunction({
      eventId,
      pluginId,
    })

    if ((result.data as any)?.success) {
      return {
        success: true,
        data: (result.data as any)?.data || {},
      }
    } else {
      // Fallback to localStorage in studio mode if backend fails
      if (typeof window !== "undefined") {
        const storageKey = `plugin_event_data_${eventId}_${pluginId}`
        const cached = localStorage.getItem(storageKey)
        if (cached) {
          try {
            return {
              success: true,
              data: JSON.parse(cached),
            }
          } catch {
            // Continue to return empty data if parsing fails
          }
        }
      }

      return {
        success: true,
        data: {},
      }
    }
  } catch (error) {
    console.error("Error getting plugin event instance data:", error)

    // Fallback to localStorage in studio mode if backend fails
    if (typeof window !== "undefined") {
      const storageKey = `plugin_event_data_${eventId}_${pluginId}`
      const cached = localStorage.getItem(storageKey)
      if (cached) {
        try {
          return {
            success: true,
            data: JSON.parse(cached),
          }
        } catch {
          // Continue to return empty data if parsing fails
        }
      }
    }

    return {
      success: false,
      data: {},
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export const savePluginEventInstanceData = async (
  eventId: string,
  pluginId: string,
  data: any,
): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `[pluginDataApi] Saving plugin event instance data for: ${pluginId} in event: ${eventId}`,
    )

    // Call the backend function for event instance data
    const savePluginEventInstanceDataFunction = functionsAccessor.httpsCallable(
      "pluginManagement-savePluginEventInstanceData",
    )
    const result = await savePluginEventInstanceDataFunction({
      eventId,
      pluginId,
      data,
    })

    if ((result.data as any)?.success) {
      return {
        success: true,
        data: (result.data as any)?.data || data,
      }
    } else {
      // Fallback to localStorage in studio mode if backend fails
      if (typeof window !== "undefined") {
        const storageKey = `plugin_event_data_${eventId}_${pluginId}`
        localStorage.setItem(storageKey, JSON.stringify(data))
      }

      return {
        success: false,
        data: {},
        error:
          (result.data as any)?.error ||
          "Failed to save plugin event instance data",
      }
    }
  } catch (error) {
    console.error("Error saving plugin event instance data:", error)

    // Fallback to localStorage in studio mode if backend fails
    if (typeof window !== "undefined") {
      const storageKey = `plugin_event_data_${eventId}_${pluginId}`
      localStorage.setItem(storageKey, JSON.stringify(data))
    }

    return {
      success: false,
      data: {},
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export const getEventPluginUserData = async (
  eventId: string,
  pluginId: string,
  userId: string,
): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `[pluginDataApi] Getting event plugin user data for user: ${userId}, plugin: ${pluginId}, event: ${eventId}`,
    )

    // Call the backend function for user-specific plugin data
    const getEventPluginUserDataFunction = functionsAccessor.httpsCallable(
      "pluginManagement-getEventPluginUserData",
    )
    const result = await getEventPluginUserDataFunction({
      eventId,
      pluginId,
      userId,
    })

    if ((result.data as any)?.success) {
      return {
        success: true,
        data: (result.data as any)?.data || {},
      }
    } else {
      // Fallback to localStorage in studio mode if backend fails
      if (typeof window !== "undefined") {
        const storageKey = `plugin_user_data_${eventId}_${pluginId}_${userId}`
        const cached = localStorage.getItem(storageKey)
        if (cached) {
          try {
            return {
              success: true,
              data: JSON.parse(cached),
            }
          } catch {
            // Continue to return empty data if parsing fails
          }
        }
      }

      return {
        success: true,
        data: {},
      }
    }
  } catch (error) {
    console.error("Error getting event plugin user data:", error)

    // Fallback to localStorage in studio mode if backend fails
    if (typeof window !== "undefined") {
      const storageKey = `plugin_user_data_${eventId}_${pluginId}_${userId}`
      const cached = localStorage.getItem(storageKey)
      if (cached) {
        try {
          return {
            success: true,
            data: JSON.parse(cached),
          }
        } catch {
          // Continue to return empty data if parsing fails
        }
      }
    }

    return {
      success: false,
      data: {},
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export const saveEventPluginUserData = async (
  eventId: string,
  pluginId: string,
  userId: string,
  data: any,
): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `[pluginDataApi] Saving event plugin user data for user: ${userId}, plugin: ${pluginId}, event: ${eventId}`,
    )

    // Call the backend function for user-specific plugin data
    const saveEventPluginUserDataFunction = functionsAccessor.httpsCallable(
      "pluginManagement-saveEventPluginUserData",
    )
    const result = await saveEventPluginUserDataFunction({
      eventId,
      pluginId,
      userId,
      data,
    })

    if ((result.data as any)?.success) {
      return {
        success: true,
        data: (result.data as any)?.data || data,
      }
    } else {
      // Fallback to localStorage in studio mode if backend fails
      if (typeof window !== "undefined") {
        const storageKey = `plugin_user_data_${eventId}_${pluginId}_${userId}`
        localStorage.setItem(storageKey, JSON.stringify(data))
      }

      return {
        success: false,
        data: {},
        error:
          (result.data as any)?.error ||
          "Failed to save event plugin user data",
      }
    }
  } catch (error) {
    console.error("Error saving event plugin user data:", error)

    // Fallback to localStorage in studio mode if backend fails
    if (typeof window !== "undefined") {
      const storageKey = `plugin_user_data_${eventId}_${pluginId}_${userId}`
      localStorage.setItem(storageKey, JSON.stringify(data))
    }

    return {
      success: false,
      data: {},
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

/**
 * Production implementation of the raw plugin API
 */
export const productionRawApi: RawPluginApi = {
  getPluginConfig,
  savePluginConfig,
  getPluginEventInstanceData,
  savePluginEventInstanceData,
  getEventPluginUserData,
  saveEventPluginUserData,
}
